import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, Loader2, Store, BadgeCheck, ChevronLeft, CreditCard, LogIn, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD } from "../../lib/marketplace";
import { apiGetCarrito, apiCheckout } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

const soles = (n) => `S/ ${Number(n || 0).toFixed(2)}`;

export default function CarritoPage() {
  const { user } = useAuth();
  const { remove, refresh } = useCart();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [quitando, setQuitando] = useState(null);
  const [pagando, setPagando] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  const cargar = useCallback(async () => {
    try { setData(await apiGetCarrito()); } catch { setData(null); }
  }, []);

  useEffect(() => {
    if (user && user !== false) cargar();
    else if (user === false) setData(null);
  }, [user, cargar]);

  if (user === false) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-4" data-testid="carrito-login">
        <ShoppingCart size={40} className="mx-auto" style={{ color: MKT_GOLD }} />
        <h1 className="mt-4 text-xl font-extrabold text-slate-900">Tu carrito te espera</h1>
        <p className="mt-2 text-slate-500">Inicia sesión para agregar productos y cursos del Marketplace.</p>
        <Link to="/login" data-testid="carrito-ir-login" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold" style={{ backgroundColor: MKT_GOLD }}>
          <LogIn size={16} /> Iniciar sesión
        </Link>
      </div>
    );
  }

  if (data === undefined) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="carrito-loading"><Loader2 className="animate-spin mr-2" /> Cargando carrito…</div>;
  }

  const items = data?.items || [];

  const quitar = async (it) => {
    setQuitando(`${it.tipo}-${it.item_id}`);
    try {
      const d = await remove(it.tipo, it.item_id);
      setData(d);
      toast.success("Eliminado del carrito.");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setQuitando(null); }
  };

  const pagar = async () => {
    if (!aceptaTerminos) {
      toast.error("Debes aceptar los Términos y la Política de reembolsos para continuar.");
      return;
    }
    setPagando(true);
    try {
      const r = await apiCheckout(window.location.origin, true, "2026-07");
      if (r.url) {
        window.location.href = r.url;
      } else if (r.gratis) {
        await refresh();
        toast.success("¡Listo! Tus recursos gratuitos ya están en Mis Compras.");
        navigate(`/tienda/pago/exito?order_id=${r.order_id}`);
      }
    } catch (e) {
      toast.error(humanizeAxiosError(e));
      setPagando(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="carrito-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/tienda")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
          <ChevronLeft size={16} /> Seguir explorando
        </button>
        <h1 className="mt-3 text-2xl md:text-3xl font-extrabold text-slate-900 inline-flex items-center gap-2">
          <ShoppingCart size={26} style={{ color: MKT_GOLD }} /> Tu carrito
        </h1>

        {items.length === 0 ? (
          <div className="mt-8 text-center py-16 rounded-2xl border border-dashed border-slate-300 bg-white" data-testid="carrito-vacio">
            <PackageOpen size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">Tu carrito está vacío.</p>
            <Link to="/tienda" className="mt-4 inline-block font-bold" style={{ color: MKT_GOLD }}>Explorar el Marketplace</Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3" data-testid="carrito-items">
              {items.map((it) => (
                <div key={`${it.tipo}-${it.item_id}`} className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid={`carrito-item-${it.item_id}`}>
                  <Link to={`/tienda/${it.tipo === "curso" ? "curso" : "producto"}/${it.item_id}`} className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">
                    {it.cover ? <img src={it.cover} alt={it.titulo} className="w-full h-full object-cover" /> : <Store size={24} style={{ color: MKT_GOLD }} />}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: MKT_GOLD }}>{it.tipo === "curso" ? "Curso" : "Producto"}</p>
                    <Link to={`/tienda/${it.tipo === "curso" ? "curso" : "producto"}/${it.item_id}`} className="font-bold text-slate-900 truncate block hover:underline">{it.titulo}</Link>
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1 truncate"><Store size={12} /> {it.vendedor || it.tienda}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-extrabold text-slate-900">{it.gratuito ? "Gratis" : soles(it.subtotal)}</p>
                    <button onClick={() => quitar(it)} disabled={quitando === `${it.tipo}-${it.item_id}`} data-testid={`carrito-quitar-${it.item_id}`} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-red-600 disabled:opacity-50">
                      {quitando === `${it.tipo}-${it.item_id}` ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Quitar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-4" data-testid="carrito-resumen">
                <h2 className="font-extrabold text-slate-900">Resumen</h2>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Artículos</span><span data-testid="carrito-cantidad">{data.cantidad}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Comisión de plataforma{data.comision_pct != null ? ` (${data.comision_pct}%)` : ""}</span><span>{soles(data.comision_total)}</span></div>
                  <div className="border-t border-slate-100 pt-2 flex justify-between text-base font-extrabold text-slate-900"><span>Total</span><span data-testid="carrito-total">{soles(data.total)}</span></div>
                </div>
                <label className="mt-4 flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="carrito-terminos-label">
                  <input type="checkbox" checked={aceptaTerminos} onChange={(e) => setAceptaTerminos(e.target.checked)} data-testid="carrito-acepta-terminos" className="mt-0.5 accent-[#D97706] w-4 h-4 flex-shrink-0" />
                  <span>Acepto los <Link to="/legal/terminos" target="_blank" className="font-semibold underline" style={{ color: MKT_GOLD }}>Términos</Link> y la <Link to="/legal/reembolsos" target="_blank" className="font-semibold underline" style={{ color: MKT_GOLD }}>Política de reembolsos</Link>.</span>
                </label>
                <button onClick={pagar} disabled={pagando || !aceptaTerminos} data-testid="carrito-pagar" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold shadow-sm hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: MKT_GOLD }}>
                  {pagando ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Proceder al pago
                </button>
                <p className="mt-2 text-[11px] text-slate-400 text-center">Pago seguro con Mercado Pago.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
